import { Bookmark, Lightbulb, Share2 } from "lucide-react";
import React, { useState } from "react";

import Spinner from "@/app/components/Spinner.jsx";
import { Button } from "@/app/components/ui/button.js";

import { JobDetails } from "../utils/helpers";

interface JobActionButtonsProps {
	jobDetails: JobDetails;
	submitting: boolean;
	explainerIsLoading: boolean;
	roleExplanations: unknown | null;
	onSave: () => void;
	onShare: () => void;
	onRoleExplainer: () => void;
	onNotRelevant: () => void;
}

export function JobActionButtons({
	jobDetails,
	submitting: _submitting,
	explainerIsLoading,
	roleExplanations,
	onSave,
	onShare,
	onRoleExplainer,
	onNotRelevant,
}: JobActionButtonsProps) {
	const [showShareMenu, setShowShareMenu] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const [isMarkingIrrelevant, setIsMarkingIrrelevant] = useState(false);

	const hasExplanation = roleExplanations !== null;

	// Separate handlers for save and not relevant to avoid shared loading state
	const handleSave = () => {
		setIsSaving(true);
		onSave();
		// Reset after a short delay (the actual interaction is handled elsewhere)
		setTimeout(() => setIsSaving(false), 1000);
	};

	const handleNotRelevant = () => {
		setIsMarkingIrrelevant(true);
		onNotRelevant();
		setTimeout(() => setIsMarkingIrrelevant(false), 1000);
	};

	return (
		<div className="space-y-4 mb-8">
			{/* Primary CTA: Explain this role */}
			<div className="bg-gradient-to-r from-primary-50 to-blue-50 rounded-xl p-5 border border-primary-100">
				<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
					<div className="flex-1">
						<h3 className="text-lg font-semibold text-gray-900 mb-1">
							{hasExplanation ? "Role explained" : "Want a clearer picture of this role?"}
						</h3>
						<p className="text-sm text-gray-600">
							{hasExplanation
								? "Scroll down to see the explanation, or generate a fresh one."
								: "Get a clear, simple explanation of what this job really involves."}
						</p>
					</div>
					<Button onClick={onRoleExplainer} disabled={explainerIsLoading} size="sm">
						{explainerIsLoading ? (
							<div className="flex items-center gap-2">
								<span className="w-4 h-4">
									<Spinner />
								</span>
								<span>Explaining...</span>
							</div>
						) : (
							<span className="flex items-center gap-2">
								<Lightbulb className="h-4 w-4" />
								{hasExplanation ? "Refresh explanation" : "Explain this role for me"}
							</span>
						)}
					</Button>
				</div>
			</div>

			{/* Secondary Actions Row */}
			<div className="flex items-center justify-between">
				<div className="flex items-center gap-2">
					{/* Save Button */}
					<button
						disabled={isSaving}
						className="inline-flex items-center justify-center gap-2 bg-gray-100 text-gray-700 px-4 h-9 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium disabled:opacity-50"
						onClick={handleSave}
					>
						<Bookmark className="h-4 w-4" />
						<span>{isSaving ? "Saving..." : "Save"}</span>
					</button>

					{/* Share Button */}
					<div className="relative">
						<button
							className="inline-flex items-center justify-center gap-2 bg-gray-100 text-gray-700 px-4 h-9 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
							onClick={() => {
								setShowShareMenu(!showShareMenu);
								if (!showShareMenu) {
									onShare();
								}
							}}
						>
							<Share2 className="h-4 w-4" />
							<span>Share</span>
						</button>

						{/* Share dropdown menu */}
						{showShareMenu && (
							<div className="absolute top-full left-0 mt-2 bg-white rounded-lg shadow-lg border border-gray-200 p-3 z-10">
								<ShareLinks jobDetails={jobDetails} onClose={() => setShowShareMenu(false)} />
							</div>
						)}
					</div>
				</div>

				{/* Right side actions */}
				<div className="flex items-center gap-2">
					<Button onClick={handleNotRelevant} variant="ghost" size="sm" disabled={isMarkingIrrelevant}>
						{isMarkingIrrelevant ? "..." : "Not Relevant"}
					</Button>
				</div>
			</div>
		</div>
	);
}

// Share links component
interface ShareLinksProps {
	jobDetails: JobDetails;
	onClose: () => void;
}

function ShareLinks({ jobDetails, onClose }: ShareLinksProps) {
	const baseUrl = window.location.origin;
	const shareUrl = `${baseUrl}/jobs/${jobDetails?._id}`;
	const shareText = `Check out this opportunity for a ${jobDetails?.jobTitle} at ${jobDetails?.company?.company_name || ""}`;
	const encodedText = encodeURIComponent(shareText);
	const encodedUrl = encodeURIComponent(shareUrl);

	const shareLinks = [
		{
			name: "WhatsApp",
			url: `https://wa.me/?text=${encodedText}%20${encodedUrl}`,
			icon: "fab fa-whatsapp",
			color: "text-green-600",
		},
		{
			name: "LinkedIn",
			url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
			icon: "fab fa-linkedin",
			color: "text-blue-700",
		},
		{
			name: "Twitter",
			url: `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`,
			icon: "fa-brands fa-x-twitter",
			color: "text-gray-900",
		},
		{
			name: "Facebook",
			url: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
			icon: "fab fa-facebook",
			color: "text-blue-600",
		},
	];

	return (
		<div className="flex gap-3">
			{shareLinks.map((link) => (
				<a
					key={link.name}
					href={link.url}
					target="_blank"
					rel="noopener noreferrer"
					className={`${link.color} hover:opacity-80 transition-opacity`}
					title={`Share on ${link.name}`}
					onClick={onClose}
				>
					<i className={`${link.icon} text-xl`} />
				</a>
			))}
		</div>
	);
}
